<template>
  <main class="pb-8">
    <Notice :operation="operation" />
    <div
      v-if="loading"
      class="fixed bg-black/70 w-full h-full inset-0 z-50 flex justify-center items-center p-6 text-white font-bold"
    >
      Validation en cours ...
    </div>
    <div
      v-if="error"
      class="fixed bg-black/70 w-full h-full inset-0 z-50 flex justify-center items-center p-6 text-white font-bold"
    >
      <div
        class="bg-red-50 text-red-700 w-full max-w-sm p-4 rounded m-auto text-center"
      >
        Une erreur est survenue
        <p class="font-normal p-4">
          {{ errorMessage || "Vérifiez les champs que vous avez renseignés" }}
        </p>
        <button @click="error = false; errorMessage = ''" class="button !bg-red-700 mb-2">
          Fermer
        </button>
      </div>
    </div>

    <!-- Message si l'opération n'est pas active -->
    <div v-if="operation && !isActive" class="max-w-3xl md:m-auto mx-6 mt-20 mb-10" style="margin-top: 150px;">
      <div class="bg-red-100 border border-red-300 text-red-800 p-6 rounded text-center">
        <p class="font-bold text-lg mb-2">Cette opération n'est pas disponible actuellement.</p>
        <p class="text-sm">L'offre n'est pas encore ouverte ou a déjà pris fin.</p>
        <router-link to="/">
          <button class="button uppercase font-bold mt-4">Retour à l'accueil</button>
        </router-link>
      </div>
    </div>

    <div v-if="operation && operation.files && operation.files.length > 0 && isActive">
      <img :src="operation.files[0].uri" class="w-full max-w-7xl m-auto" />
    </div>

    <div v-if="operation && isActive" class="max-w-3xl md:m-auto mx-6">
      <h2
        class="font-bold pt-6 text-xl w-full text-center my-6 pb-2 text-[#283A61]"
      >
        {{ operation.title.split(" - ")[1] }}
      </h2>

      <div
        v-if="operation.counters && operation.counters.length == 1"
        class="w-full"
      >
        <div
          class="p-2 rounded w-full"
          :class="
            operation.counter > 0
              ? 'bg-green-100 border-green-300 text-green-800'
              : 'bg-red-100 border-red-300 text-red-800'
          "
        >
          <span v-if="operation.counter > 0"
            ><b>{{ operation.counter }} participation restantes</b> pour
            bénéficier du remboursement*</span
          >
          <span class="text-sm block !leading-2" v-else>
            Le compteur de participation a été atteint, vous ne pouvez plus
            participer à l'offre de remboursement.</span
          >
        </div>
      </div>
      <div
        v-if="operation.counters && operation.counters.length == 2"
        class="bg-green-50 text-green-800 w-full p-4 rounded"
      >
        <span v-if="operation.counter > 0">
          <b>{{ operation.counter }} participation restantes</b> pour bénéficier
          du remboursement pour la référence 305*</span
        ><br />
        <span v-if="operation.counter2 > 0">
          <b>{{ operation.counter2 }} participation restantes</b> pour
          bénéficier du remboursement pour la référence 310*</span
        >
      </div>
      <div
        v-if="done"
        class="max-w-3xl p-6 bg-gray-100 rounded m-auto text-[#283A61] mt-4 mb-2"
        v-html="
          operation.custom.text_submited ||
          operation.custom.lotperdu_informations
        "
      />
      <div class="m-auto text-center">
        <router-link v-if="done" to="/"
          ><button class="button text-centr uppercase font-bold mt-4">
            retour
          </button></router-link
        >
      </div>
      <Form
        v-if="!done"
        class="form_step"
        id="createForm"
        :form_id="operation.forms[0]._id"
        :server="server"
        v-on:results="getResult"
        v-on:form_submit_loading="onLoading"
        :hideError="hiderror"
        @on_error="showErrorForm"
      />

    </div>
  </main>
</template>

<script setup lang="ts">
import Notice from "@/components/NoticeView";
import { ref, onMounted } from "vue";
import { useRoute } from "vue-router/composables";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

const route = useRoute();
const server = ref("https://api.husqvarna-promo.fr");
const id = ref(route.params._id);
const operation = ref(null);
const hiderror = ref(true);
const loading = ref(false);
const done = ref(false);
const error = ref(false);
const errorMessage = ref("");
const isActive = ref(false);

const hostname = window.location.hostname;

const isDateBetween = (startDate, endDate) => {
  const todayStart = dayjs().startOf('day');
  const todayEnd = dayjs().endOf('day');
  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).endOf('day');

  // date_debut 00h00 <= aujourd'hui 00h00 ET aujourd'hui 23h59 <= date_fin 23h59
  return !todayStart.isBefore(start) && !todayEnd.isAfter(end);
};

const checkOperationActive = (data) => {
  const isTestEnv = hostname === 'test.husqvarna-operation30ans.fr' || hostname === 'localhost';

  // Sur les environnements de test, autoriser toutes les opérations
  if (isTestEnv) {
    return true;
  }

  // En production : vérifier que l'opération est en cours
  let active = isDateBetween(data.date_debut, data.date_fin);

  // Vérifier si date_fin_regule est passée
  if (data.date_fin_regule && active) {
    const dateFinRegule = dayjs(data.date_fin_regule);
    if (dayjs().isAfter(dateFinRegule.endOf('day'))) {
      active = false;
    }
  }

  return active;
};


const fetchOperation = async (_id) => {
  try {
    const response = await fetch(
      `https://api.husqvarna-promo.fr/api/v2/formdata/${_id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`Erreur HTTP: ${response.status}`);
    }

    const data = await response.json();
    let counter;
    let counter2;
    if (data.counters && data.counters.length > 0) {
      counter = await fetch(
        `https://api.husqvarna-promo.fr/api/v2/counters/${data._id}/${data.counters[0].name}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      counter = await counter.json();
      if (counter.value < 0) counter.value = 0;
      data.counter = counter.value;
    }

    if (data.counters && data.counters.length > 1) {
      counter2 = await fetch(
        `https://api.husqvarna-promo.fr/api/v2/counters/${data._id}/${data.counters[1].name}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      counter2 = await counter2.json();
      if (counter2.value < 0) counter2.value = 0;
      data.counter2 = counter2.value;
    }

    localStorage.setItem("operationId", data._id);
    localStorage.setItem("idgame", data.idgame);
    return data;
  } catch (error) {
    console.error(`Erreur lors de la requête pour l'ID ${_id}:`, error);
    throw error;
  }
};

const onLoading = () => {
  loading.value = true;
};

const showErrorForm = (event) => {
  // 1. Couper le loading immédiatement
  loading.value = false;
  error.value = true;

  // 2. Erreurs spécifiques (tableau)
  if (event && Array.isArray(event.error)) {
    const knownErrors = {
      "duplicate_email": "Un dossier avec cette adresse email existe déjà.",
      "duplicate_serial": "Ce numéro de série a déjà été utilisé.",
      "counter_limit": "Le nombre maximum de participations a été atteint.",
    };
    const matched = event.error.find((e) => knownErrors[e]);
    if (matched) {
      errorMessage.value = knownErrors[matched];
      return;
    }
  }

  // 3. Erreur objet avec message
  if (event && event.error && event.error.message) {
    errorMessage.value = event.error.message;
    return;
  }

  // 4. Erreur avec ID
  if (event && event.errorId) {
    errorMessage.value = `Une erreur est survenue (réf: ${event.errorId})`;
    return;
  }

  // 5. Fallback final
  errorMessage.value = event?.message ?? "Erreur inconnue, veuillez réessayer.";
};

const getResult = (result) => {
  if (result) {
    done.value = true;
    loading.value = false;
  } else {
    error.value = true;
  }
};

onMounted(async () => {
  try {
    operation.value = await fetchOperation(id.value);

    // Vérifier si l'opération est active
    isActive.value = checkOperationActive(operation.value);

    if (operation.value.forms && operation.value.forms.length > 0) {
      operation.value.forms = operation.value.forms.filter(
        (form) => form.type === "CREATE"
      );
    }

    if (operation.value.files && operation.value.files.length > 0) {
      operation.value.files = operation.value.files.filter(
        (file) => file.filename === "banner_form"
      );
    }
  } catch (e) {
    console.error(e);
  }
});
</script>
